import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * TripInquiryModal — captures interest in a trip that doesn't have firm dates yet.
 * Sends to `POST /api/trips/{tripId}/inquire` and notifies the admin via email.
 */
export const TripInquiryModal = ({ trip, open, onOpenChange, defaultName = '', defaultEmail = '', defaultPhone = '' }) => {
  const [form, setForm] = useState({
    name: defaultName,
    email: defaultEmail,
    phone: defaultPhone,
    preferred_dates: '',
    group_size: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/trips/${trip.id}/inquire`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        preferred_dates: form.preferred_dates.trim() || null,
        group_size: form.group_size ? parseInt(form.group_size, 10) : null,
        notes: form.notes.trim() || null,
      });
      toast.success("Thanks! We've got your interest — we'll be in touch as soon as dates firm up.");
      onOpenChange(false);
      // Reset only the volatile fields, keep name/email for convenience
      setForm((f) => ({ ...f, preferred_dates: '', group_size: '', notes: '' }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="trip-inquiry-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading text-[#0A2342]">
            Inquire about {trip?.destination}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            We're gathering interest before locking dates. Share a few details and we'll reach out as soon as we have firm dates that suit the group.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Your name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="inquiry-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="inquiry-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+27..."
                data-testid="inquiry-phone-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Group size</label>
              <Input
                type="number"
                min="1"
                value={form.group_size}
                onChange={(e) => setForm({ ...form, group_size: e.target.value })}
                placeholder="How many divers?"
                data-testid="inquiry-group-size-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Preferred dates (window)</label>
            <Input
              value={form.preferred_dates}
              onChange={(e) => setForm({ ...form, preferred_dates: e.target.value })}
              placeholder="e.g. Long weekends in Sep–Oct 2026, school holidays, etc."
              data-testid="inquiry-dates-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Cert level, gear hire, special requests…"
              data-testid="inquiry-notes-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="inquiry-cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
              data-testid="inquiry-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : 'Send Enquiry'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TripInquiryModal;
